// src/components/mitchRoom/revenueAnalytics/MonthStrip.js
//
// Jul · Aug · … · Jun · Season pill strip for one business season (1 Jul –
// 30 Jun; mirrors REVENUE_SEASON_START_MONTH in services/api/config/seasons.py).
// Each month is closed (final vs LY), current (live pacing + forecast) or
// future (forecast only, muted with a badge). Replaces the old behaviour
// where the only way to see another month was dragging the Overview end
// date backwards.
import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';

export const SEASON_START_MONTH = 7; // July
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Start year of the season containing a calendar (year, month 1-12)
export function seasonYearOf(year, month) {
  return month >= SEASON_START_MONTH ? year : year - 1;
}

export function seasonMonths(seasonYear) {
  return Array.from({ length: 12 }, (_, i) => {
    const m0 = SEASON_START_MONTH - 1 + i; // 0-based month index, may run past December
    const year = seasonYear + Math.floor(m0 / 12);
    const month = (m0 % 12) + 1;
    return { key: `${year}-${String(month).padStart(2, '0')}`, label: MONTH_LABELS[month - 1] };
  });
}

export function monthState(key, currentMonth) {
  if (key < currentMonth) return 'closed';
  if (key === currentMonth) return 'current';
  return 'future';
}

export default function MonthStrip({
  seasonYear,
  selected, // 'YYYY-MM' | 'season'
  currentMonth, // 'YYYY-MM'
  onSelect,
  onSeasonChange,
  minSeasonYear,
  maxSeasonYear,
}) {
  const months = seasonMonths(seasonYear);
  const seasonLabel = `${seasonYear}/${String(seasonYear + 1).slice(2)}`;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-token-large border border-borderGrey bg-white p-2">
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          aria-label="Previous season"
          disabled={minSeasonYear !== undefined && seasonYear <= minSeasonYear}
          onClick={() => onSeasonChange?.(seasonYear - 1)}
        >
          <ChevronLeft size={14} />
        </Button>
        <span className="min-w-[64px] text-center text-token-md font-semibold">{seasonLabel}</span>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          aria-label="Next season"
          disabled={maxSeasonYear !== undefined && seasonYear >= maxSeasonYear}
          onClick={() => onSeasonChange?.(seasonYear + 1)}
        >
          <ChevronRight size={14} />
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-1">
        {months.map((m) => {
          const state = monthState(m.key, currentMonth);
          const active = selected === m.key;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => onSelect?.(m.key)}
              title={
                state === 'future'
                  ? `${m.label} — forecast only`
                  : state === 'current'
                    ? `${m.label} — live pacing + forecast`
                    : `${m.label} — final vs last year`
              }
              className={cn(
                'rounded-token-large border px-2.5 py-1 text-token-sm transition-colors',
                active
                  ? 'border-primary bg-primary text-white'
                  : 'border-borderGrey bg-white hover:bg-neutral-50',
                !active && state === 'future' && 'text-muted-foreground',
                !active && state === 'current' && 'border-primary/60 font-semibold text-primary'
              )}
            >
              {m.label}
              {state === 'future' && !active && (
                <span className="ml-1 text-[10px] uppercase tracking-wide opacity-70">fcst</span>
              )}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => onSelect?.('season')}
          className={cn(
            'ml-1 rounded-token-large border px-3 py-1 text-token-sm font-semibold transition-colors',
            selected === 'season'
              ? 'border-primary bg-primary text-white'
              : 'border-borderGrey bg-white hover:bg-neutral-50'
          )}
        >
          Season
        </button>
      </div>
    </div>
  );
}
